import { Alert, Backdrop, Box, CircularProgress } from '@mui/material';
import UPMultiselectDropdown from 'components/app-container/multiselect-dropdown';
import UPButton from 'components/button';
import DeleteModal from 'components/delete-modal';
import { UPDropdownItem } from 'components/dropdown';
import { UPInputLabel } from 'components/input/styles';
import { DateFormat } from 'constants/DateFormat';
import {
  ContentContainer,
  EmployeeInfoSmallBox,
  EmployeeModalInfoContainer,
  ModalCloseIcon,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalWrapper,
} from 'modules/employees/add-edit-employee-modal/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getLocationsAction } from 'store/actions/company-locations-actions';
import {
  clearApplicantDetailAction,
  discardApplicantAction,
  downloadApplicantCvAction,
  getApplicantDetailAction,
  promoteApplicantAction,
  restoreApplicantAction,
  updateApplicantBranchesAction,
} from 'store/actions/job-applicant-actions';
import { locationsSelector } from 'store/selectors/company-locations-selectors';
import {
  applicantActionErrorSelector,
  applicantActionLoadingSelector,
  applicantCvDownloadErrorSelector,
  applicantDetailErrorSelector,
  applicantDetailLoadingSelector,
  applicantDetailSelector,
} from 'store/selectors/job-applicant-selectors';
import theme from 'theme';
import {
  APPLICANTS__CV,
  APPLICANTS__CV_DOWNLOAD_FAILED,
  APPLICANTS__DETAIL_LOAD_FAILED,
  APPLICANTS__DISCARD_BUTTON,
  APPLICANTS__DISCARD_CONFIRM_MESSAGE,
  APPLICANTS__EXISTING_EMPLOYEE_INDICATOR,
  APPLICANTS__MOTIVATIONAL_TEXT,
  APPLICANTS__PROMOTE_BUTTON,
  APPLICANTS__RESTORE_BUTTON,
  APPLICANTS__SUBMITTED_ON,
  APPLICANTS__VIEW_APPLICANT,
  APPLICANTS__VIEW_CV,
  EMPLOYEE__COMPANY_LOCATION,
  GENERAL__EMAIL,
  GENERAL__ERROR_MESSAGE,
  GENERAL__FIRST_NAME,
  GENERAL__LAST_NAME,
  GENERAL__PHONE_NUMBER,
  GENERAL__RETRY,
} from 'translations/constants';
import { ApplicantStatus } from 'types/employee/IJobApplicant';
import dayjs from 'utils/dayjs';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { DisabledMotivationTextArea, DiscardButtonStyled, ReadOnlyInput, ViewCvLinkButton } from './styles';
import { ApplicantDetailsModalProps } from './types';

const ApplicantDetailsModal = (props: ApplicantDetailsModalProps): JSX.Element => {
  const { open, onClose, applicantId, companyId } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const [openDiscardConfirm, setOpenDiscardConfirm] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<UPDropdownItem[]>([]);

  const detail = useAppSelector(applicantDetailSelector);
  const detailLoading = useAppSelector(applicantDetailLoadingSelector);
  const detailError = useAppSelector(applicantDetailErrorSelector);
  const actionLoading = useAppSelector(applicantActionLoadingSelector);
  const actionError = useAppSelector(applicantActionErrorSelector);
  const cvDownloadError = useAppSelector(applicantCvDownloadErrorSelector);
  const locations = useAppSelector(locationsSelector);

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!actionError && !cvDownloadError) return;
    errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [actionError, cvDownloadError]);

  const fetchDetail = () => {
    dispatch(getApplicantDetailAction({ companyId, id: applicantId }));
  };

  // Employees.tsx's own location fetch (getLocationsDropdownItemsAction) is manager-scoped —
  // it can come back empty for a caller with no personal branch assignments, e.g. Admin. This
  // modal just needs every branch name for display, so it fetches the plain, unscoped company
  // locations list (same one the admin Locations.tsx page uses) into its own state slice.
  useEffect(() => {
    fetchDetail();
    dispatch(getLocationsAction(companyId));
    return () => {
      dispatch(clearApplicantDetailAction());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId, companyId]);

  const locationItems: UPDropdownItem[] = useMemo(
    () => locations.map(location => ({ value: String(location.id), label: location.name })),
    [locations],
  );

  // Resyncs whenever a fresh detail loads — including right after a successful branch save,
  // which is what clears the dirty state and hides the Save button again.
  useEffect(() => {
    if (!detail) return;
    setSelectedBranches(locationItems.filter(item => detail.locationIds.includes(Number(item.value))));
  }, [detail, locationItems]);

  const branchesDirty = useMemo(() => {
    if (!detail) return false;
    const saved = [...detail.locationIds].map(String).sort().join(',');
    const selected = selectedBranches
      .map(item => item.value)
      .sort()
      .join(',');
    return saved !== selected;
  }, [detail, selectedBranches]);

  const onDiscard = () => {
    dispatch(discardApplicantAction({ companyId, id: applicantId }))
      .unwrap()
      .then(success => {
        if (success) onClose();
      });
  };

  const onRestore = () => {
    dispatch(restoreApplicantAction({ companyId, id: applicantId }))
      .unwrap()
      .then(success => {
        if (success) onClose();
      });
  };

  // Branch edits have no standalone save — a changed selection is only persisted as part of
  // promoting. If that branch update fails, stop there and leave the applicant un-promoted
  // rather than promoting against a branch set the manager never got to see confirmed.
  const onPromote = async () => {
    if (branchesDirty) {
      const branchesSaved = await dispatch(
        updateApplicantBranchesAction({
          companyId,
          id: applicantId,
          locationIds: selectedBranches.map(item => Number(item.value)),
        }),
      ).unwrap();
      if (!branchesSaved) return;
    }
    const promoted = await dispatch(promoteApplicantAction({ companyId, id: applicantId })).unwrap();
    if (promoted) onClose();
  };

  const onViewCv = () => {
    if (!detail?.cvFileName) return;
    dispatch(downloadApplicantCvAction({ companyId, applicantId }));
  };

  return (
    <>
      <Backdrop sx={{ zIndex: '1' }} open={open}>
        <ModalWrapper open={open} onClose={onClose}>
          <ModalContainer>
            <ModalHeader>
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                <ModalTitle>{t(APPLICANTS__VIEW_APPLICANT)}</ModalTitle>
                <ModalCloseIcon onClick={onClose}>
                  <CloseIcon fill={theme.palette.secondary.contrastText} />
                </ModalCloseIcon>
              </Box>
            </ModalHeader>
            <ContentContainer disableScroll={false}>
              {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!detailLoading && detailError && (
                <Box>
                  <Alert severity="error" icon={false} variant="filled">
                    {t(APPLICANTS__DETAIL_LOAD_FAILED)}
                  </Alert>
                  <Box sx={{ marginTop: '12px' }}>
                    <UPButton text={t(GENERAL__RETRY)} onClick={fetchDetail} outlined />
                  </Box>
                </Box>
              )}
              {!detailLoading && !detailError && detail && (
                <>
                  <EmployeeModalInfoContainer sx={{ marginTop: 0 }}>
                    <EmployeeInfoSmallBox sx={{ width: '50%' }}>
                      <ReadOnlyInput
                        value={detail.firstName}
                        onChange={() => null}
                        label={t(GENERAL__FIRST_NAME)}
                        placeholder={t(GENERAL__FIRST_NAME)}
                        disabled
                      />
                    </EmployeeInfoSmallBox>
                    <EmployeeInfoSmallBox sx={{ width: '50%' }}>
                      <ReadOnlyInput
                        value={detail.lastName}
                        onChange={() => null}
                        label={t(GENERAL__LAST_NAME)}
                        placeholder={t(GENERAL__LAST_NAME)}
                        disabled
                      />
                    </EmployeeInfoSmallBox>
                  </EmployeeModalInfoContainer>
                  <EmployeeModalInfoContainer sx={{ marginTop: 0 }}>
                    <EmployeeInfoSmallBox sx={{ width: '40%' }}>
                      <ReadOnlyInput
                        value={detail.phoneNumber}
                        onChange={() => null}
                        label={t(GENERAL__PHONE_NUMBER)}
                        placeholder={t(GENERAL__PHONE_NUMBER)}
                        disabled
                      />
                    </EmployeeInfoSmallBox>
                    <EmployeeInfoSmallBox sx={{ width: '60%' }}>
                      <ReadOnlyInput
                        value={detail.emailAddress}
                        onChange={() => null}
                        label={t(GENERAL__EMAIL)}
                        placeholder={t(GENERAL__EMAIL)}
                        disabled
                      />
                    </EmployeeInfoSmallBox>
                  </EmployeeModalInfoContainer>
                  <EmployeeModalInfoContainer sx={{ marginTop: 0 }}>
                    <EmployeeInfoSmallBox sx={{ width: '100%' }}>
                      <UPMultiselectDropdown
                        items={locationItems}
                        handleChange={value => setSelectedBranches(value ?? [])}
                        value={selectedBranches}
                        label={t(EMPLOYEE__COMPANY_LOCATION)}
                        placeholder={t(EMPLOYEE__COMPANY_LOCATION)}
                        disabled={actionLoading || detail.status !== ApplicantStatus.Submitted}
                      />
                    </EmployeeInfoSmallBox>
                  </EmployeeModalInfoContainer>
                  {detail.existsAsEmployeeInCompany && (
                    <Alert severity="info" icon={false}>
                      {t(APPLICANTS__EXISTING_EMPLOYEE_INDICATOR)}
                    </Alert>
                  )}
                  <Box>
                    <UPInputLabel>{t(APPLICANTS__MOTIVATIONAL_TEXT)}</UPInputLabel>
                    <DisabledMotivationTextArea value={detail.motivationalText} readOnly disabled minRows={3} />
                  </Box>
                  <EmployeeModalInfoContainer sx={{ marginTop: 0 }}>
                    {detail.cvFileName && (
                      <EmployeeInfoSmallBox sx={{ width: '50%' }}>
                        <UPInputLabel>{t(APPLICANTS__CV)}</UPInputLabel>
                        <ViewCvLinkButton onClick={onViewCv}>{t(APPLICANTS__VIEW_CV)}</ViewCvLinkButton>
                      </EmployeeInfoSmallBox>
                    )}
                    <EmployeeInfoSmallBox sx={{ width: '50%' }}>
                      <ReadOnlyInput
                        value={dayjs(detail.submittedDate).format(DateFormat.FULL_DATE)}
                        onChange={() => null}
                        label={t(APPLICANTS__SUBMITTED_ON)}
                        placeholder={t(APPLICANTS__SUBMITTED_ON)}
                        disabled
                      />
                    </EmployeeInfoSmallBox>
                  </EmployeeModalInfoContainer>
                  {(cvDownloadError || actionError) && (
                    <Box ref={errorRef} sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                      {cvDownloadError && (
                        <Alert severity="error" icon={false} variant="filled">
                          {t(APPLICANTS__CV_DOWNLOAD_FAILED)}
                        </Alert>
                      )}
                      {actionError && (
                        <Alert severity="error" icon={false} variant="filled">
                          {t(actionError ?? GENERAL__ERROR_MESSAGE)}
                        </Alert>
                      )}
                    </Box>
                  )}
                </>
              )}
            </ContentContainer>
            <ModalFooter>
              {detail?.status === ApplicantStatus.Submitted && (
                <>
                  <Box sx={{ marginLeft: '12px' }}>
                    <DiscardButtonStyled
                      text={t(APPLICANTS__DISCARD_BUTTON)}
                      onClick={() => setOpenDiscardConfirm(true)}
                      disabled={actionLoading}
                      outlined
                    />
                  </Box>
                  <Box sx={{ marginLeft: '12px' }}>
                    <UPButton text={t(APPLICANTS__PROMOTE_BUTTON)} onClick={onPromote} disabled={actionLoading} />
                  </Box>
                </>
              )}
              {detail?.status === ApplicantStatus.Discarded && (
                <Box sx={{ marginLeft: '12px' }}>
                  <UPButton text={t(APPLICANTS__RESTORE_BUTTON)} onClick={onRestore} disabled={actionLoading} />
                </Box>
              )}
            </ModalFooter>
          </ModalContainer>
        </ModalWrapper>
      </Backdrop>
      {openDiscardConfirm && (
        <DeleteModal
          open={openDiscardConfirm}
          onClose={() => setOpenDiscardConfirm(false)}
          content={t(APPLICANTS__DISCARD_CONFIRM_MESSAGE)}
          onDelete={onDiscard}
        />
      )}
    </>
  );
};

export default ApplicantDetailsModal;
